import { ThemeToggle } from './ThemeToggle';
import { useLocation } from 'preact-iso';
import { useAuth } from '../state/auth';
import { UserAvatar } from './UserAvatar';
import { IcHome, IcPaddle, IcCourt, IcClub, IcUser, IcPlus, IcBell, IcSearch } from './icons';
import { isEn, otherLangHref, stripLang } from '../lib/i18n';
import { cx } from '../lib/format';

const TABS = [
  { href: '/', label: 'หน้าแรก', icon: IcHome, match: (p: string) => p === '/' },
  { href: '/sessions', label: 'หาตี้', icon: IcPaddle, match: (p: string) => p.startsWith('/sessions') },
  { href: '/courts', label: 'สนาม', icon: IcCourt, match: (p: string) => p.startsWith('/courts') },
  { href: '/clubs', label: 'คลับ', icon: IcClub, match: (p: string) => p.startsWith('/clubs') },
  { href: '/me', label: 'ฉัน', icon: IcUser, match: (p: string) => p.startsWith('/me') },
];

/** Desktop (≥720px): small input that submits to /search?q= · mobile: icon → /search */
function TopSearch() {
  const { route, path, query } = useLocation();
  const onSearch = path === '/search';
  return (
    <>
      <form
        class="topsearch"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          const q = (new FormData(e.currentTarget as HTMLFormElement).get('q') as string | null)?.trim() ?? '';
          route(`/search${q ? `?q=${encodeURIComponent(q)}` : ''}`);
        }}
      >
        <IcSearch size={16} class="ic" />
        <input name="q" type="search" placeholder="ค้นหาคลับ #แท็ก สนาม ผู้เล่น" aria-label="ค้นหา" autocomplete="off" defaultValue={onSearch ? query.q ?? '' : ''} />
      </form>
      <a class={cx('iconbtn topsearch-btn', onSearch && 'on')} href="/search" aria-label="ค้นหา">
        <IcSearch />
      </a>
    </>
  );
}

/**
 * TH ⇄ EN, on the page you are already on.
 *
 * A real link, not a toggle: it is the other language's URL, so it can be copied, opened in a new tab,
 * and read by a crawler as the alternate version of this page. `data-lang="switch"` tells the link
 * prefixer in lib/i18n to leave this one alone — it is the one link that is meant to change language.
 */
function LangSwitch() {
  const en = isEn();
  const href = otherLangHref();
  return (
    <a
      class="iconbtn"
      style={{ fontWeight: 700, fontSize: 13, letterSpacing: '0.02em' }}
      href={href}
      data-lang="switch"
      /*
       * A full page load, not a client-side route: the language is installed once per load (it is what
       * decides every string on the way to the DOM), so switching has to start the app again. Stopping
       * the event here keeps preact-iso's own link handler from turning it into a soft navigation.
       */
      onClick={(e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.assign(href);
      }}
      hrefLang={en ? 'th' : 'en'}
      aria-label={en ? 'อ่านเป็นภาษาไทย' : 'Read this page in English'}
      title={en ? 'ภาษาไทย' : 'English'}
    >
      {en ? 'TH' : 'EN'}
    </a>
  );
}

export function TopBar() {
  // The address carries the language (/en/courts); the tabs are matched on the path without it.
  const path = stripLang(useLocation().path);
  const { me, loading, openLogin } = useAuth();
  return (
    <header class="topbar">
      <div class="topbar-inner">
        <a class="logo" href="/" aria-label="DINKR หน้าแรก">
          <img class="logo-day" src="/brand/dinkr-2.png" alt="DINKR" /><img class="logo-night" src="/brand/dinkr-1.png" alt="DINKR" />
        </a>
        <nav class="topnav" aria-label="เมนูหลัก">
          {TABS.slice(0, 4).map((t) => (
            <a key={t.href} href={t.href} class={cx(t.match(path) && 'on')} aria-current={t.match(path) ? 'page' : undefined}>
              {t.label}
            </a>
          ))}
          <a href="/players" class={cx(path.startsWith('/players') && 'on')}>
            ผู้เล่น
          </a>
        </nav>
        <div class="topbar-actions">
          <TopSearch />
          <LangSwitch />
          <ThemeToggle />
          <a class="btn primary sm" href="/sessions/new">
            <IcPlus size={16} /> เปิดตี้
          </a>
          {!loading && me && (
            <a class="iconbtn" href="/me/notifications" aria-label={`แจ้งเตือน${me.unread_notifications ? ` ${me.unread_notifications} รายการ` : ''}`}>
              <IcBell />
              {me.unread_notifications > 0 && <span class="notification-count" aria-hidden="true">{me.unread_notifications > 99 ? '99+' : me.unread_notifications}</span>}
            </a>
          )}
          {!loading && me ? (
            <a href="/me" aria-label="โปรไฟล์ของฉัน" style={{ display: 'inline-flex' }}>
              <UserAvatar user={me} size="sm" />
            </a>
          ) : !loading ? (
            <button type="button" class="btn secondary sm" onClick={() => openLogin()}>
              เข้าสู่ระบบ
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

export function TabBar() {
  const path = stripLang(useLocation().path);
  return (
    <nav class="tabbar" aria-label="แท็บหลัก">
      {TABS.map((t) => {
        const Icon = t.icon;
        const on = t.match(path);
        return (
          <a key={t.href} href={t.href} class={cx(on && 'on')} aria-current={on ? 'page' : undefined}>
            <Icon />
            <span>{t.label}</span>
          </a>
        );
      })}
    </nav>
  );
}

export function Footer() {
  return (
    <footer class="footer">
      <a href="/how-it-works">วิธีใช้</a>
      <a href="/faq">คำถามที่พบบ่อย</a>
      <a href="/privacy">ความเป็นส่วนตัว</a>
      <a href="/terms">ข้อกำหนด</a>
      <div class="mt-8">DINKR Open Play · นัดตีพิกเคิลบอล</div>
    </footer>
  );
}
